import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle2, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from '../lib/brand';
import { useAuth } from '../lib/auth-context';

/**
 * Pagina "Primo accesso / Reset password".
 *
 * Flow:
 *   1. Mount: GET /api/auth/setup-account/:token → verifica il token, carica
 *      email + nome + ruolo per la UI. Se non valido → fallback con messaggio.
 *   2. Utente sceglie una password (≥ 8 char + 1 maiuscola + 1 numero).
 *   3. POST /api/auth/setup-account/:token { new_password } → backend ritorna
 *      JWT + user; auth-context lo installa, l'utente arriva su /app.
 *
 * Lo stesso flusso serve sia per il primo accesso (link inviato dall'admin)
 * sia per il reset password (link inviato dalla pagina di login "Password
 * dimenticata"). Il backend distingue solo nel template email, non nell'UX.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const buildRules = (t) => [
  { test: (p) => p.length >= 8,          label: t('setupAccount.rule8', 'Almeno 8 caratteri') },
  { test: (p) => /[A-Z]/.test(p),        label: t('setupAccount.ruleUpper', 'Una lettera maiuscola') },
  { test: (p) => /[0-9]/.test(p),        label: t('setupAccount.ruleNumber', 'Un numero') },
  { test: (p) => /[^A-Za-z0-9]/.test(p), label: t('setupAccount.ruleSymbol', 'Un simbolo (consigliato)'), soft: true },
];

export default function SetupAccountPage() {
  const { t } = useTranslation();
  const rules = buildRules(t);
  const { token } = useParams();
  const nav = useNavigate();
  const auth = useAuth();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadToken = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND}/api/auth/setup-account/${token}`);
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setTokenError(body.detail || t('setupAccount.invalidLinkHttp', 'Link non valido (HTTP {{status}})', { status: r.status }));
        return;
      }
      setUser(await r.json());
    } catch (_e) {
      setTokenError(t('setupAccount.networkErrorRetry', 'Errore di rete. Riprova fra qualche secondo.'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadToken(); }, [loadToken]);

  const requiredRulesPassed = rules.filter((r) => !r.soft).every((r) => r.test(pw));
  const matches = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (!requiredRulesPassed) {
      toast.error(t('setupAccount.pwRequirements', 'La password non rispetta tutti i requisiti.'));
      return;
    }
    if (!matches) {
      toast.error(t('setupAccount.pwMismatch', 'Le due password non coincidono.'));
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/auth/setup-account/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: pw }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(body.detail || t('setupAccount.errorHttp', 'Errore ({{status}})', { status: r.status }));
        setSubmitting(false);
        return;
      }
      toast.success(t('setupAccount.pwSet', 'Password impostata. Benvenuto/a nel CRM!'));
      if (body.token && body.user) {
        auth.setSession?.({ token: body.token, user: body.user });
      }
      // Affiliates land in the dedicated dashboard, everyone else in /app.
      const dest = body.user?.role === 'affiliate' ? '/affiliate/dashboard' : '/app';
      setTimeout(() => nav(dest), 800);
    } catch (_e) {
      toast.error(t('setupAccount.networkError', 'Errore di rete. Riprova.'));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--surface-2)] p-6">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" />
          <span>{t('setupAccount.verifying', 'Verifico il link…')}</span>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--surface-2)] p-6">
        <Card className="max-w-lg w-full border-border" data-testid="setup-error-card">
          <CardContent className="p-8 text-center space-y-4">
            <img src={BRAND.greenParadiseLogoSmall} alt="Green Paradise" className="h-12 w-auto mx-auto" />
            <h1 className="font-serif text-2xl text-[hsl(var(--primary))]">{t('setupAccount.invalidLink', 'Link non valido')}</h1>
            <p className="text-sm text-muted-foreground">{tokenError}</p>
            <p className="text-xs text-muted-foreground">{t('setupAccount.useForgot', 'Usa la funzione “Password dimenticata?” dalla pagina di login per richiedere un nuovo link.')}</p>
            <Button onClick={() => nav('/login')} className="mt-2" data-testid="setup-back-login">
              {t('setupAccount.goLogin', 'Vai al login')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-2)] flex flex-col">
      <header className="px-6 pt-8 pb-4">
        <div className="max-w-2xl mx-auto flex flex-col items-center text-center">
          <img src={BRAND.greenParadiseLogoSmall} alt="Green Paradise" className="h-12 w-auto" />
          <div className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground mt-2">HCF Group</div>
          <h1 className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))] mt-2">
            {t('setupAccount.title', 'Imposta la tua password')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">{t('setupAccount.subtitle', 'Stai impostando la password personale per accedere al CRM Green Paradise. Sarà l’unica che userai d’ora in poi.')}</p>
        </div>
      </header>

      <main className="flex-1 px-4 pb-8">
        <div className="max-w-md mx-auto">
          <Card className="border-border">
            <CardContent className="p-6 md:p-8 space-y-5">
              <div className="bg-[var(--surface-2)] border border-border rounded-md p-3 text-sm">
                <div className="text-muted-foreground text-xs uppercase tracking-wide mb-1">{t('setupAccount.account', 'Account')}</div>
                <div className="font-medium">{user?.name}</div>
                <div className="text-xs font-mono text-muted-foreground break-all">{user?.email}</div>
                {user?.role && (
                  <div className="text-xs text-muted-foreground mt-1">{t('setupAccount.crmRole', 'Ruolo CRM:')} <b>{user.role}</b></div>
                )}
              </div>

              <form className="space-y-4" onSubmit={submit}>
                <div className="space-y-1.5">
                  <Label htmlFor="pw">{t('setupAccount.newPw', 'Nuova password')}</Label>
                  <div className="relative">
                    <Input
                      id="pw"
                      type={showPw ? 'text' : 'password'}
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      autoFocus
                      autoComplete="new-password"
                      data-testid="setup-pw-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      aria-label={showPw ? t('setupAccount.hidePw', 'Nascondi password') : t('setupAccount.showPw', 'Mostra password')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="pw2">{t('setupAccount.repeatPw', 'Ripeti la password')}</Label>
                  <Input
                    id="pw2"
                    type={showPw ? 'text' : 'password'}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    autoComplete="new-password"
                    data-testid="setup-pw2-input"
                  />
                  {pw2 && !matches && (
                    <p className="text-xs text-destructive">{t('setupAccount.pwMismatchShort', 'Le password non coincidono.')}</p>
                  )}
                </div>

                <ul className="text-xs space-y-1">
                  {rules.map((r) => {
                    const ok = r.test(pw);
                    return (
                      <li key={r.label} className={ok
                        ? 'text-[hsl(var(--primary))] flex items-center gap-1.5'
                        : r.soft
                          ? 'text-muted-foreground flex items-center gap-1.5'
                          : 'text-foreground/70 flex items-center gap-1.5'}>
                        {ok
                          ? <CheckCircle2 className="w-3.5 h-3.5" />
                          : <span className="w-3.5 h-3.5 inline-block border border-border rounded-full" />}
                        {r.label}
                      </li>
                    );
                  })}
                </ul>

                <Button
                  type="submit"
                  disabled={submitting || !requiredRulesPassed || !matches}
                  className="w-full"
                  data-testid="setup-submit-button"
                >
                  {submitting ? t('setupAccount.setting', 'Impostazione…') : t('setupAccount.submit', 'Imposta password e accedi')}
                </Button>

                <div className="flex items-start gap-2 text-xs text-muted-foreground pt-3 border-t border-border">
                  <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{t('setupAccount.linkInfo', 'Link personale, valido per il tempo indicato nell’email. Si invalida automaticamente una volta usato. Se non eri tu a richiederlo, ignora questa pagina: nessuna azione richiesta.')}</span>
                </div>
              </form>
            </CardContent>
          </Card>
          <div className="text-center text-xs text-muted-foreground mt-4">
            Green Paradise · HCF Group · {new Date().getFullYear()}
          </div>
        </div>
      </main>
    </div>
  );
}
